/**
 * Gallery Scroll Sync
 *
 * Handles scroll behavior for product information media gallery:
 *  - carousel  → advances the slideshow-component to the next slide as user scrolls details
 *  - grid      → when product details is NOT sticky, reverses the sticky behavior so that:
 *                1) Media gallery stays sticky/fixed at the top while details column scrolls FIRST.
 *                2) Once details column finishes scrolling (reaches bottom edge), details becomes
 *                   sticky at bottom while media gallery releases and scrolls down.
 */

(function () {
  'use strict';

  /**
   * Initialise scroll sync for one product grid.
   * @param {HTMLElement} grid
   */
  function initGrid(grid) {
    const mediaWrapper = grid.querySelector('.product-information__media');
    const detailsCol = grid.querySelector('.product-details');
    const gallery = grid.querySelector('media-gallery');

    if (!mediaWrapper || !detailsCol || !gallery) return;

    const presentation = gallery.dataset.presentation || 'carousel';

    // ── Slide advancement (carousel mode) ──────────────────────────────────
    if (presentation === 'carousel') {
      const slideshow = gallery.querySelector('slideshow-component');
      if (!slideshow) return;

      let currentIndex = 0;
      let slideCount = 0;

      requestAnimationFrame(() => {
        slideCount =
          parseInt(slideshow.dataset.slideCount, 10) ||
          slideshow.querySelectorAll('slideshow-slide').length ||
          1;
      });

      function onScroll() {
        if (slideCount <= 1) return;

        const gridRect = grid.getBoundingClientRect();
        const scrolledPast = Math.max(0, -gridRect.top);
        const scrollable = Math.max(1, detailsCol.offsetHeight - window.innerHeight);
        const progress = Math.min(1, scrolledPast / scrollable);

        const targetIndex = Math.min(
          slideCount - 1,
          Math.floor(progress * slideCount)
        );

        if (targetIndex !== currentIndex) {
          currentIndex = targetIndex;
          if (typeof slideshow.select === 'function') {
            slideshow.select(currentIndex);
          } else {
            const dot = gallery.querySelector(
              `.slideshow-controls [data-index="${currentIndex}"], ` +
              `.slideshow-controls button:nth-child(${currentIndex + 1})`
            );
            dot?.click();
          }
        }
      }

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });

      new MutationObserver((_, obs) => {
        if (!grid.isConnected) {
          window.removeEventListener('scroll', onScroll);
          window.removeEventListener('resize', onScroll);
          obs.disconnect();
        }
      }).observe(document.body, { childList: true, subtree: true });
    }

    // ── Grid mode: Reverse Sticky when details block is not sticky ─────────
    if (presentation === 'grid') {
      const isDetailsSticky =
        detailsCol.classList.contains('sticky-content') ||
        detailsCol.classList.contains('sticky-content--desktop');

      // Only apply reverse sticky behavior if details column is NOT sticky
      if (!isDetailsSticky) {
        function getBounds() {
          const headerOffset =
            parseInt(getComputedStyle(grid).getPropertyValue('--sticky-header-offset'), 10) ||
            parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10) ||
            0;
          const detailsHeight = detailsCol.offsetHeight;
          const maxTop = headerOffset;
          const minTop = Math.floor(Math.min(maxTop, window.innerHeight - detailsHeight - 24));
          return { headerOffset, maxTop, minTop, detailsHeight };
        }

        function updateReverseSticky() {
          if (!grid.isConnected) return;

          const isDesktop = window.matchMedia('(min-width: 992px)').matches;
          if (!isDesktop) {
            grid.classList.remove('product-information__grid--reverse-sticky');
            mediaWrapper.style.removeProperty('height');
            detailsCol.style.removeProperty('top');
            return;
          }

          grid.classList.add('product-information__grid--reverse-sticky');

          const { headerOffset, minTop, detailsHeight } = getBounds();
          const mediaHeight = gallery.offsetHeight;

          // S is the scroll distance required for details column to finish scrolling to its bottom edge
          const S = Math.max(0, detailsHeight - window.innerHeight + headerOffset + 24);
          const targetHeight = Math.ceil(mediaHeight + S);

          // Set permanent downward sticky behavior; on upward scroll from bottom both naturally scroll together
          detailsCol.style.top = `${minTop}px`;

          // Only update style if height difference is meaningful to prevent ResizeObserver loops
          if (Math.abs(mediaWrapper.offsetHeight - targetHeight) > 2) {
            mediaWrapper.style.height = `${targetHeight}px`;
          }
        }

        const ro = new ResizeObserver(() => {
          requestAnimationFrame(updateReverseSticky);
        });

        ro.observe(gallery);
        ro.observe(detailsCol);
        window.addEventListener('resize', updateReverseSticky, { passive: true });
        updateReverseSticky();

        new MutationObserver((_, obs) => {
          if (!grid.isConnected) {
            window.removeEventListener('resize', updateReverseSticky);
            ro.disconnect();
            obs.disconnect();
          }
        }).observe(document.body, { childList: true, subtree: true });
      }
    }
  }

  /**
   * Find and initialise all product grids on the page.
   */
  function initAll() {
    document
      .querySelectorAll('.product-information__grid:not([data-scroll-sync-init])')
      .forEach((grid) => {
        grid.dataset.scrollSyncInit = '1';
        initGrid(grid);
      });
  }

  // Run on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init after Shopify theme editor section reloads
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('.product-information__grid[data-scroll-sync-init]').forEach((g) => {
      delete g.dataset.scrollSyncInit;
    });
    initAll();
  });

  // Re-init if the page content changes (e.g. infinite scroll, SPA navigation)
  document.addEventListener('page:load', initAll);   // Turbo
  document.addEventListener('page:change', initAll); // older liquid ajax
})();
